import React, { useCallback, useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import {
  Modal as AntModal,
  Button,
  Form as AntForm,
  Col,
  notification,
  Space,
  Typography
} from 'antd'
import * as Icons from '@ant-design/icons'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import moment from 'moment'

import { BodyContentColumn } from 'admin/lib/Layouts'
import { CertificateTag } from 'admin/lib/Certificates/CertificateTag'

import {
  SectionProvider,
  Section,
  FormItem,
  FormRow,
  Select
} from 'admin/lib/Forms'

import * as ROUTES from 'admin/routes'
import { useApi, Queries } from 'admin/lib/Api'
import { Guid } from 'admin/lib/guid'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'

export function EditReferenceProductItemForm({ referenceProductItem, isNew }) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  const certificates = Queries.useCertificates()
  const companies = Queries.useCompanies()

  //
  // Form setup & values

  const blankRefItem = useMemo(() => {
    return {
      referenceProductId: Guid.Empty,
      name: '',
      eTag: '',
      createdAtDate: '0001-01-01T00:00:00Z',
      certificateIds: [],
      companyIds: []
    }
  }, [])

  const form = useForm({
    defaultValues: blankRefItem,
    mode: 'all',
    criteriaMode: 'firstError'
  })

  const draftItem = isNew ? blankRefItem : referenceProductItem

  const { reset } = form
  useEffect(() => {
    reset(draftItem)
  }, [draftItem, reset])

  //
  // Callbacks

  const api = useApi()
  const onSubmit = useCallback(
    async data => {
      setSubmitting(true)

      const submissionData = produce(draftItem, draft => {
        Object.assign(draft, data)
      })

      const [, status, error] = isNew
        ? await api.registerReferenceProductItem(submissionData)
        : await api.updateReferenceProductItem(submissionData)

      if (error) {
        AntModal.error({
          title: 'Error Saving Reference Product Item',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
      } else {
        setSubmitting(false)
        navigate(ROUTES.REFERENCEITEMS.LIST)
        notification.success({ message: 'Reference Product updated/created.' })
      }
    },
    [api, draftItem, isNew, navigate]
  )
  const handleSubmit = form.handleSubmit(onSubmit)

  useGlobalShortcut(Matchers.form.save, () => handleSubmit())

  const certOptions = (certificates.data ?? []).map(cert => {
    const content = `${cert.certificationScheme} (${cert.certificateNumber})
    Exp: ${
      cert.expirationDate
        ? moment(cert.expirationDate).local().format('DD/MM/YYYY')
        : ''
    } `
    return {
      key: cert.certificateId,
      value: cert.certificateId,
      label: content
    }
  })

  const certTagRender = props => {
    const { value, closable, onClose } = props
    const onPreventMouseDown = event => {
      event.preventDefault()
      event.stopPropagation()
    }
    const relatedCert = certificates.data?.find(
      cert => cert.certificateId === value
    )
    return (
      <CertificateTag
        id={value}
        cert={relatedCert}
        closable={closable}
        onClose={onClose}
        onMouseDown={onPreventMouseDown}
      />
    )
  }

  return (
    <SectionProvider>
      <FormProvider {...form}>
        <BodyContentColumn>
          <AntForm layout="vertical" onFinish={handleSubmit}>
            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={4}>
                    {isNew
                      ? 'Create Reference Product Item'
                      : 'Update Reference Product Item'}
                  </Typography.Title>

                  <FormItem
                    name="name"
                    label="Name"
                    rules={{
                      minLength: 2,
                      required: true
                    }}
                    inputRef={firstInputRef}
                  />

                  <FormRow>
                    <FormItem name="certificateIds" label="Certificates">
                      <Select
                        mode="multiple"
                        loading={!certificates.isFetched}
                        allowClear
                        showSearch
                        options={certOptions}
                        optionFilterProp="label"
                        tagRender={certTagRender}
                      ></Select>
                    </FormItem>

                    <Button
                      type="text"
                      tabIndex="-1"
                      icon={<Icons.EditFilled />}
                      href={ROUTES.CERTIFICATES.LIST}
                      target="_blank"
                    />
                  </FormRow>

                  <FormRow>
                    <FormItem name="companyIds" label="Companies">
                      <Select
                        mode="multiple"
                        loading={!companies.isFetched}
                        allowClear
                        showSearch
                      >
                        {(companies.data ?? [])
                          .sort((a, b) =>
                            a.name.localeCompare(b.name, 'en', {
                              sensitivity: 'base'
                            })
                          )
                          .map(comp => (
                            <Select.Option
                              key={comp.companyId}
                              value={comp.companyId}
                              aria-label={comp.name}
                            >
                              {comp.name}
                            </Select.Option>
                          ))}
                      </Select>
                    </FormItem>

                    <Button
                      type="text"
                      tabIndex="-1"
                      icon={<Icons.EditFilled />}
                      href={ROUTES.COMPANIES.LIST}
                      target="_blank"
                    />
                  </FormRow>
                </>
              )}
            </Section>

            <Col align="middle">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={submitting}
                  style={{ width: '8rem' }}
                >
                  Save
                </Button>

                <Link to={ROUTES.REFERENCEITEMS.LIST}>
                  <Button disabled={submitting} style={{ width: '8rem' }}>
                    Cancel
                  </Button>
                </Link>
              </Space>
            </Col>
          </AntForm>
        </BodyContentColumn>
      </FormProvider>
    </SectionProvider>
  )
}

EditReferenceProductItemForm.propTypes = {
  referenceProductItem: PropTypes.object,
  isNew: PropTypes.bool.isRequired
}
